// Saved propulsion comparisons — account-synced when signed in (mirrors the
// Log Analyzer's backend comparisons), localStorage otherwise. If the backend
// endpoints aren't deployed yet, authed calls fall back to localStorage so the
// feature keeps working; it upgrades to account sync automatically once the
// API ships (see FRONTEND-REQUESTS.md "Propulsion setup comparisons").
//
// Every function reports `source: "account" | "local"` so callers route
// follow-up writes (e.g. delete) to the same store the list came from.

import {
  listSetupComparisons,
  getSetupComparison,
  createSetupComparison,
  deleteSetupComparison,
} from "./api/comparisons";

export const COMPS_KEY = "vcs-propulsion-saved-comparisons";

export const readStoredComparisons = () => {
  try {
    const raw = localStorage.getItem(COMPS_KEY);
    const v = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

export const writeStoredComparisons = (comps) => {
  try {
    localStorage.setItem(COMPS_KEY, JSON.stringify(comps));
  } catch {
    /* storage blocked — non-fatal */
  }
};

// Display date for either store's rows (local `saved_at` or backend `created_at`).
export const comparisonDate = (c) => c.saved_at || (c.created_at || "").slice(0, 10);

export const loadComparisonSets = async (authed) => {
  if (authed) {
    try {
      return { source: "account", comparisons: await listSetupComparisons() };
    } catch {
      /* API missing/down — fall back to local below */
    }
  }
  return { source: "local", comparisons: readStoredComparisons() };
};

export const createComparisonSet = async (authed, { name, entries }) => {
  if (authed) {
    try {
      return { source: "account", comparison: await createSetupComparison({ name, entries }) };
    } catch {
      /* fall back to local */
    }
  }
  const comparison = {
    id: `cmp-${Date.now()}`,
    name,
    entries,
    saved_at: new Date().toISOString().slice(0, 10),
  };
  writeStoredComparisons([...readStoredComparisons(), comparison]);
  return { source: "local", comparison };
};

export const deleteComparisonSets = async (source, ids) => {
  if (source === "account") {
    try {
      await Promise.all(ids.map((id) => deleteSetupComparison(id)));
      return;
    } catch {
      /* fall through to local cleanup */
    }
  }
  writeStoredComparisons(readStoredComparisons().filter((c) => !ids.includes(c.id)));
};

// Resolve a ?comparison= deep link: local first (works logged out and for
// pre-sync saves), then the account store.
export const getComparisonSet = async (authed, id) => {
  const local = readStoredComparisons().find((c) => c.id === id);
  if (local) return local;
  if (authed) {
    try {
      return await getSetupComparison(id);
    } catch {
      /* not found / API missing */
    }
  }
  return null;
};
